import { formatInTimeZone as formatInTZ } from "date-fns-tz";
import addD from "date-fns/addDays";
import addMin from "date-fns/addMinutes";
import addM from "date-fns/addMonths";
import addSec from "date-fns/addSeconds";
import differenceInHours from "date-fns/differenceInHours";
import differenceInMinutes from "date-fns/differenceInMinutes";
import isAfter from "date-fns/isAfter";
import isEqual from "date-fns/isEqual";
import isSameDay from "date-fns/isSameDay";
import isValid_ from "date-fns/isValid";
import parseISO from "date-fns/parseISO";
import { toString } from "duration-fns";

import { config } from "shared/data";

import { isNumeric, isString } from "./common";

export function addDays(value: Date | number | string, amount: number) {
  return addD(parseDate(value), amount);
}

export function addMinutes(value: Date | number | string, amount: number) {
  return addMin(parseDate(value), amount);
}

export function addMonths(value: Date | number | string, amount: number) {
  return addM(parseDate(value), amount);
}

export function addSeconds(value: Date | number | string, amount: number) {
  return addSec(parseDate(value), amount);
}

export function durationToISOString(minutes: number, seconds: number) {
  return toString({
    minutes,
    seconds,
  });
}

export function formatDistanceToNow(value: Date | number | string) {
  const now = Date.now();
  const parsed = parseDate(value);
  const minutes = differenceInMinutes(now, parsed, { roundingMethod: "round" });
  const hours = differenceInHours(now, parsed, { roundingMethod: "round" });
  switch (true) {
    case minutes <= 1:
      return "1 minute ago";
    case minutes > 1 && minutes < 60:
      return `${minutes} minutes ago`;
    case hours === 1:
      return `1 hour ago`;
    case hours > 1 && hours <= 6:
      return `${hours} hours ago`;
    default:
      return formatInTimeZone(parsed);
  }
}

export function formatInTimeZone(
  value: Date | number | string,
  format: string = config.date.defaultFormat,
  timeZone: string = config.date.defaultTimeZone,
) {
  return formatInTZ(parseDate(value), timeZone, format);
}

export function isAfterOrEqual(
  value: Date | number | string,
  compareValue: Date | number | string,
) {
  const dateA = parseDate(value);
  const dateB = parseDate(compareValue);
  return isAfter(dateA, dateB) || isEqual(dateA, dateB);
}

export function isToday(value: Date | number | string) {
  return isSameDay(parseDate(value), new Date());
}

export function isYesterday(value: Date | number | string) {
  return isSameDay(parseDate(value), addDays(new Date(), -1));
}

export function parseDate(value: Date | number | string) {
  if (isString(value)) {
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : new Date(value);
  } else if (isNumeric(value)) {
    return new Date(value);
  }
  return value;
}

export function toISOString(value: Date | number | string) {
  return parseDate(value).toISOString();
}

export const getReadingTime = (
  time = 1,
  suffixSingle = " minute",
  suffixPlural = " minutes",
): string => {
  if (time > 1) {
    return `${time}${suffixPlural}`;
  }
  return `1${suffixSingle}`;
};

export function isValid(value: Date | number | string) {
  return isValid_(value);
}
